import { useNetworkHistory, useNetworkOverview } from '../../../features/locations/use-network-overview'
import { useIdOrCode } from '../../use-id-or-code'
import { Facts, Figure, Header, LedgerLink, locPath, mono, Note, Section, SkeletonRows, Status } from './ui'

const th = 'py-2.5 pr-4 text-left text-[12px] font-medium text-[var(--color-muted)]'

export function LedgerNetwork() {
  const idOrCode = useIdOrCode()
  const { data, isLoading, error } = useNetworkOverview(idOrCode)
  const history = useNetworkHistory(idOrCode)

  const header = (
    <Header
      eyebrow={
        <>
          <LedgerLink to="/locations">Locations</LedgerLink> /{' '}
          <LedgerLink to={locPath(idOrCode)}>{idOrCode}</LedgerLink> / Network
        </>
      }
      title="Network report"
      standfirst="What is answering and what is not, as last polled."
      aside={<LedgerLink to={`${locPath(idOrCode)}/network/wan-settings`}>WAN settings &rarr;</LedgerLink>}
    />
  )

  if (isLoading)
    return (
      <article>
        {header}
        <SkeletonRows />
      </article>
    )
  if (error || !data)
    return (
      <article>
        {header}
        <Note tone="error">
          {error ? `The report could not be read: ${error.message}` : 'No network data on record.'}
        </Note>
      </article>
    )

  const devices = Object.entries(data.status.data)
  const wans = data.status.wanInterfaces ?? []
  const { summary, tunnel } = data

  return (
    <article>
      {header}

      <Section
        title="Device status"
        note={
          data.status.lastPolledAt
            ? `Last polled ${data.status.lastPolledAt.replace('T', ' ').slice(0, 16)} UTC.`
            : undefined
        }
        aside={data.status.locationStatus ? <Status label={data.status.locationStatus} /> : null}
      >
        <div className="grid grid-cols-2 gap-y-6 border-b border-[var(--color-border)] py-6 sm:grid-cols-4">
          <Figure label="Devices" value={summary.total} />
          <Figure label="Up" value={summary.up} tone="ok" />
          <Figure label="Down" value={summary.down} tone={summary.down > 0 ? 'bad' : undefined} />
          <Figure label="Other" value={summary.other} />
        </div>
        {devices.length === 0 ? (
          <Note>No devices reported for this store.</Note>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th scope="col" className={th}>
                  Device
                </th>
                <th scope="col" className={`${th} text-right`}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {devices.map(([name, status]) => (
                <tr key={name} className="h-10 border-b border-[var(--color-border)]">
                  <td className="pr-4 text-[13px]" style={mono}>
                    {name}
                  </td>
                  <td className="text-right">
                    <Status label={status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="WAN interfaces">
        {wans.length === 0 ? (
          <Note>No WAN interface polls on record.</Note>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th scope="col" className={th}>
                  Interface
                </th>
                <th scope="col" className={th}>
                  Vendor
                </th>
                <th scope="col" className={th}>
                  Role
                </th>
                <th scope="col" className={`${th} text-right`}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {wans.map((w) => (
                <tr key={w.interfaceName} className="h-10 border-b border-[var(--color-border)]">
                  <td className="pr-4 text-[13px]" style={mono}>
                    {w.interfaceName}
                  </td>
                  <td className="pr-4 text-[14px] capitalize">{w.vendor}</td>
                  <td className="pr-4 text-[14px] capitalize">{w.wanType ?? '—'}</td>
                  <td className="text-right">
                    <Status label={w.vendorStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Tunnel">
        {tunnel ? (
          <Facts
            items={[
              ['Tunnel', <Status key="t" label={tunnel.tunnelStatus} />],
              ['Device', tunnel.deviceStatus ? <Status key="d" label={tunnel.deviceStatus} /> : null],
              ['VPN mode', tunnel.vpnMode],
            ]}
          />
        ) : (
          <Note>No tunnel data is held for this store.</Note>
        )}
      </Section>

      <Section title="History">
        {history.deferred ? (
          <Note>
            Poll history, client counts and drift are coming soon &mdash; this page will carry them once the ledger has
            them.
          </Note>
        ) : null}
      </Section>
    </article>
  )
}
